/**
 * Avatars du Grand Quiz du Lair : plusieurs univers dans une fenêtre de choix.
 * Valeurs enregistrées : « sonic » (roster Sonic du site), « pk:25 » (Pokémon), « an:5 » (animés).
 * Images : roster Sonic du site, illustrations officielles via PokéAPI, portraits AniList.
 */
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Avatar as RosterAvatar, ROSTER, rosterEntry } from './SonicRoster';

const PK = [[25, 'Pikachu'], [1, 'Bulbizarre'], [4, 'Salamèche'], [7, 'Carapuce'], [133, 'Évoli'], [6, 'Dracaufeu'], [9, 'Tortank'], [3, 'Florizarre'], [150, 'Mewtwo'], [151, 'Mew'],
  [94, 'Ectoplasma'], [143, 'Ronflex'], [39, 'Rondoudou'], [52, 'Miaouss'], [54, 'Psykokwak'], [129, 'Magicarpe'], [130, 'Léviator'], [132, 'Métamorph'], [196, 'Mentali'], [197, 'Noctali'], [700, 'Nymphali'],
  [249, 'Lugia'], [254, 'Jungko'], [257, 'Braségali'], [260, 'Laggron'], [282, 'Gardevoir'], [340, 'Barbicha'], [384, 'Rayquaza'], [445, 'Carchacrok'], [448, 'Lucario'], [658, 'Amphinobi'],
  [778, 'Mimiqui'], [887, 'Lanssorien']];
const PK_URL = (id) => `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${id}.png`;

const AL = 'https://s4.anilist.co/file/anilistcdn/character/large/';
const AN = [
  [5, 'Ichigo', 'b5-a7bkJgjhhigE.png'], [6, 'Rukia', 'b6-25WoBeWMZXBc.png'], [1086, 'Aizen', 'b1086-qR9218BjZTC0.png'], [17, 'Naruto', 'b17-phjcWCkRuIhu.png'], [13, 'Sasuke', 'b13-SISLEw1oAD7a.png'],
  [85, 'Kakashi', 'b85-mkVBh2yjxjmx.png'], [40, 'Luffy', 'b40-MNypXsxSRb1R.png'], [62, 'Zoro', 'b62-S7oAeA9WInjV.png'], [11, 'Edward', 'b11-TA5Nuk7EDUZG.jpg'], [12, 'Alphonse', 'b12-tCKu8yK5kFL5.jpg'],
  [68, 'Roy Mustang', 'b68-moBLY2WO2am3.png'], [176754, 'Frieren', 'b176754-PCnpqIOkjhFk.png'], [183965, 'Fern', 'b183965-uGFohBjlFoTp.png'], [184313, 'Stark', 'b184313-CQl6GSt4RSny.jpg'],
  [138100, 'Anya', 'b138100-4Li0tWRCa5bQ.png'], [138101, 'Loid', 'b138101-7NCB0Md8zA6G.png'], [138102, 'Yor', 'b138102-ZOAu9jI2d5ke.png'], [126824, 'Maomao', 'b126824-MqsCncTO1qpv.png'],
  [120649, 'Kaguya', 'b120649-NPaWaIpWy60E.png'], [121101, 'Shirogane', 'b121101-Q8HzKP15At2d.png'], [64769, 'Hinata', 'b64769-WoWlCMLLgJ14.png'], [64771, 'Kageyama', 'b64771-BuCVs7B8bBdh.png'],
  [4003, 'Jotaro', 'b4003-gWDSEGbeOAll.png'], [10529, 'Giorno', 'b10529-AloL8jjZwjsg.png'], [417, 'Lelouch', 'b417-gVLmIJu9phcK.png'], [422, 'Guts', 'b422-XTaiTuvRohsV.png'], [124142, 'Senku', 'b124142-XUO1g7wRqkaT.png'],
  [257562, 'Bocchi', 'b257562-Ru35NYPfsqhY.png'], [434, 'Onizuka', 'b434-Q0K9e4HukawA.png'], [18769, 'Kuroko', 'b18769-1SOeKdgkjBef.png'], [310, 'Sakuragi', 'b310-3GLKM2I3EiRf.png'], [16412, 'Mark Evans', 'b16412-ZsvITu2WWg6Q.jpg'],
  [507, 'Hauru', 'b507-g9yz1GTLtPla.jpg'], [498, 'Rin Tohsaka', 'b498-lwawtSpLyATL.png'], [1, 'Spike', 'b1-ChxaldmieFlQ.png'], [284158, 'David', 'b284158-z1uGKC3IYVa1.png'], [390, 'Mugen', 'b390-30DZg8NvIrVk.png'],
  [127221, 'Sakuta', 'n127221-Wxg4xFtW4kaJ.png'], [133676, 'Marin', 'b133676-kV2czE3C8Qls.png']
];

export const UNIVERSES = [
  { id: 'sonic', name: 'Sonic', icon: '💨', list: ROSTER.map((r) => ({ v: r.id, name: r.name })) },
  { id: 'pk', name: 'Pokémon', icon: '⚡', list: PK.map(([id, name]) => ({ v: `pk:${id}`, name, src: PK_URL(id) })) },
  { id: 'an', name: 'Animés', icon: '🌸', list: AN.map(([id, name, f]) => ({ v: `an:${id}`, name, src: AL + f })) }
];
const ALL = new Map(UNIVERSES.flatMap((u) => u.list.map((x) => [x.v, { ...x, u: u.id }])));
export const avatarInfo = (v) => ALL.get(v) || null;
export const randomAvatar = () => { const l = [...ALL.keys()]; return l[Math.floor(Math.random() * l.length)]; };

/** Pastille d'avatar pour n'importe quel univers (ou une image libre pour les anciennes parties). */
export function AnyAvatar({ v, name, size = 56, mediaUrl }) {
  const [bad, setBad] = useState(false);
  if (v && rosterEntry(v)) return <span className="qs-avatar is-roster" style={{ width: size, height: size }}><RosterAvatar id={v} size={size} fallbackName={name} /></span>;
  const info = avatarInfo(v);
  const src = info ? info.src : v && /^(https?:|\/)/.test(v) ? (mediaUrl ? mediaUrl(v) : v) : '';
  if (src && !bad) return <img className={`qs-avatar${info && info.u === 'pk' ? ' is-pk' : ''}`} src={src} alt={info ? info.name : name} referrerPolicy="no-referrer" onError={() => setBad(true)} style={{ width: size, height: size }} />;
  return <span className="qs-avatar is-initials" style={{ width: size, height: size }}>{(name || info?.name || '?').slice(0, 2).toUpperCase()}</span>;
}

/** Fenêtre de choix : un onglet par univers, recherche, grille. */
export function AvatarModal({ value, onPick, onClose }) {
  const [tab, setTab] = useState(() => avatarInfo(value)?.u || 'sonic');
  const [q, setQ] = useState('');
  const u = UNIVERSES.find((x) => x.id === tab);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return u.list;
    return UNIVERSES.flatMap((x) => x.list).filter((x) => x.name.toLowerCase().includes(s));
  }, [q, u]);
  // Rendu à la racine de la page : un parent animé (transform, filtre) piégerait la fenêtre
  const host = (typeof document !== 'undefined' && document.querySelector('.qs-root')) || document.body;
  return createPortal(
    <div className="qs-modal" onClick={onClose} role="presentation">
      <div className="qs-modal-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Choisir un personnage">
        <header>
          <h3>Choisis ton personnage</h3>
          <button type="button" className="qs-modal-x" onClick={onClose} aria-label="Fermer">✕</button>
        </header>
        <div className="qs-modal-tabs">
          {UNIVERSES.map((x) => <button type="button" key={x.id} className={x.id === tab && !q ? 'is-on' : ''} onClick={() => { setTab(x.id); setQ(''); }}><span>{x.icon}</span>{x.name}<small>{x.list.length}</small></button>)}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" />
        </div>
        <div className="qs-modal-grid">
          {list.map((x) => (
            <button type="button" key={x.v} className={`qs-av-slot${x.v === value ? ' is-mine' : ''}`} onClick={() => { onPick(x.v); onClose(); }}>
              <AnyAvatar v={x.v} name={x.name} size={64} />
              <span>{x.name}</span>
            </button>
          ))}
          {!list.length && <p className="qs-muted">Aucun personnage trouvé.</p>}
        </div>
      </div>
    </div>,
    host
  );
}

/** Portrait de présentateur, en grand. */
export function Portrait({ v, name }) {
  const [bad, setBad] = useState(false);
  if (v && rosterEntry(v)) return <div className="qs-portrait is-roster"><RosterAvatar id={v} size={210} fallbackName={name} /></div>;
  const info = avatarInfo(v);
  const src = info ? info.src : v && /^(https?:|\/)/.test(v) ? v : '';
  return (
    <div className={`qs-portrait${info && info.u === 'pk' ? ' is-pk' : ''}`}>
      {src && !bad ? <img src={src} alt={name} referrerPolicy="no-referrer" onError={() => setBad(true)} /> : <span>{(name || '?').slice(0, 2).toUpperCase()}</span>}
    </div>
  );
}
